// User Service for AIMetaPic V2
// Handles user data operations including credit balance

import { AuthCore } from '../core/AuthCore';
import { useAuthStore } from '../stores/authStore';

// Security constants
const DEMO_STORAGE_LIMIT = 1000000000; // 1GB
const REQUEST_TIMEOUT = 10000; // 10 seconds

// API Response Types
interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
}

interface UserProfileApiData {
  id: string;
  name: string;
  profile_image?: string;
  credits: number;
  created_at: string;
  updated_at: string;
}

interface UserStatsApiData {
  total_uploads: number;
  credits_used: number;
  files_processed: number;
}

interface UserProfileResponse {
  user: UserProfileApiData;
  stats?: UserStatsApiData;
}

// Public Interfaces
export interface UserData {
  id: string;
  name: string;
  avatar_url?: string;
  credits: number;
  created_at: string;
  updated_at: string;
}

export interface UserStatsData {
  totalFiles: number;
  totalStorage: number;
  storageLimit: number;
  creditsUsedThisMonth: number;
  filesProcessedThisMonth: number;
  averageCreditsPerFile: number;
}

class UserService {
  private userDataCache: UserData | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 30000; // 30 seconds
  
  /**
   * Check if cached data is still valid
   */
  private isCacheValid(): boolean {
    return this.userDataCache !== null && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
  }
  
  /**
   * Clear cache
   */
  private clearCache(): void {
    this.userDataCache = null;
    this.cacheTimestamp = 0;
  }
  
  /**
   * Validate user authentication state
   */
  private async validateAuthentication(): Promise<{ token: string; user: any }> {
    // Get Supabase token from AuthCore
    const token = await AuthCore.getInstance().getAccessToken();
    
    if (!token || typeof token !== 'string' || token.trim().length === 0) {
      throw new Error('Authentication required');
    }

    const authState = useAuthStore.getState();
    const user = authState.user;
    
    if (!user || !user.id) {
      throw new Error('User session invalid');
    }

    return { token, user };
  }

  /**
   * Centralized method to fetch user profile using backend API
   */
  private async fetchUserProfile(signal?: AbortSignal): Promise<ApiResponse<UserProfileResponse>> {
    const { token } = await this.validateAuthentication();
    
    // Use the backend API endpoint instead of direct Supabase query
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';
    const response = await fetch(`${apiBaseUrl}/user/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      signal
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed');
      }
      throw new Error(`Failed to fetch user profile: ${response.status}`);
    }
    
    const apiResponse = await response.json();
    
    // Backend returns { success: true, data: { user: {...} } }
    if (!apiResponse.success || !apiResponse.data) {
      throw new Error('Invalid response structure from backend');
    }
    
    return apiResponse;
  }

  /**
   * Sanitize user data to prevent XSS and other security issues
   */
  private sanitizeUserData(userData: UserProfileApiData): UserData {
    if (!userData || typeof userData !== 'object') {
      throw new Error('Invalid user data structure');
    }

    const credits = Number(userData.credits) || 0;

    return {
      id: String(userData.id || '').replace(/[<>"'&]/g, ''),
      name: String(userData.name || '').replace(/[<>"'&]/g, ''),
      avatar_url: userData.profile_image ? String(userData.profile_image).replace(/[<>"'&]/g, '') : undefined,
      credits: credits,
      created_at: String(userData.created_at || '').replace(/[<>"'&]/g, ''),
      updated_at: String(userData.updated_at || '').replace(/[<>"'&]/g, '')
    };
  }

  /**
   * Get current user data from backend
   */
  async getCurrentUserData(signal?: AbortSignal): Promise<UserData> {
    // Return cached data if valid
    if (this.isCacheValid()) {
      return this.userDataCache!;
    }
    
    try {
      await this.validateAuthentication();
    } catch (error: any) {
      this.clearCache();
      throw new Error(`Authentication failed: ${error.message}`);
    }

    try {
      const response = await this.fetchUserProfile(signal);
      
      if (!response?.success || !response.data) {
        throw new Error('Invalid response from server');
      }
      
      // Backend returns data.user, not just data
      const userRawData = response.data.user || response.data;
      const userData = this.sanitizeUserData(userRawData);
      
      // Cache the result
      this.userDataCache = userData;
      this.cacheTimestamp = Date.now();
      
      return userData;
    } catch (error: any) {
      this.clearCache();
      if (error?.message === 'Request timeout') {
        throw new Error('Request timeout - please try again');
      }
      if (error?.message?.includes('Authentication failed')) {
        throw error; // Re-throw authentication errors as-is
      }
      throw new Error('Failed to fetch user data');
    }
  }

  /**
   * Get demo stats for unauthenticated users
   */
  private getDemoStats(): UserStatsData {
    return {
      totalFiles: 0,
      totalStorage: 0,
      storageLimit: DEMO_STORAGE_LIMIT,
      creditsUsedThisMonth: 0,
      filesProcessedThisMonth: 0,
      averageCreditsPerFile: 0
    };
  }

  /**
   * Fetch user stats with proper typing
   */
  private async fetchUserStats(signal?: AbortSignal): Promise<ApiResponse<UserStatsApiData>> {
    // Return demo stats for now since we don't have stats table yet
    const response = {
      success: true,
      data: {
        total_uploads: 0,
        credits_used: 0,
        files_processed: 0
      }
    };

    return response;
  }

  /**
   * Sanitize stats data from API response
   */
  private sanitizeStatsData(statsData: UserStatsApiData): UserStatsData {
    if (!statsData || typeof statsData !== 'object') {
      return this.getDemoStats();
    }

    const totalFiles = Math.max(0, Number(statsData.total_uploads) || 0);
    const creditsUsed = Math.max(0, Number(statsData.credits_used) || 0);
    const filesProcessed = Math.max(0, Number(statsData.files_processed) || 0);

    return {
      totalFiles,
      totalStorage: 0, // Not available in current API
      storageLimit: DEMO_STORAGE_LIMIT,
      creditsUsedThisMonth: creditsUsed,
      filesProcessedThisMonth: filesProcessed,
      averageCreditsPerFile: filesProcessed > 0 ? creditsUsed / filesProcessed : 0
    };
  }

  /**
   * Get user statistics for dashboard display
   */
  async getUserStats(signal?: AbortSignal): Promise<UserStatsData> {
    try {
      await this.validateAuthentication();
    } catch (error) {
      // Return demo stats for unauthenticated users without logging sensitive info
      return this.getDemoStats();
    }

    try {
      const response = await this.fetchUserStats(signal);
      
      if (!response?.success || !response.data) {
        return this.getDemoStats();
      }
      
      return this.sanitizeStatsData(response.data);
    } catch (error: any) {
      // Handle timeout and other errors gracefully
      if (error?.message === 'Request timeout') {
        console.error('Stats request timeout');
      } else {
        console.error('Stats fetch failed');
      }
      
      return this.getDemoStats();
    }
  }

  /**
   * Get current credit balance using new unified system - using backend API
   */
  async getCreditBalance(): Promise<number> {
    const { token } = await this.validateAuthentication();
    
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';
    const response = await fetch(`${apiBaseUrl}/credits/balance`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch credit balance');
    }
    
    const apiResponse = await response.json();
    
    if (!apiResponse.success || apiResponse.data?.credits === undefined) {
      throw new Error('Invalid response structure');
    }
    
    // Use the new unified credit system - backend will return the correct balance
    // based on membership status (monthly + topup for members, free for non-members)
    return Number(apiResponse.data.credits) || 0;
  }
}

export const userService = new UserService();
export default userService;